import { Helmet } from 'react-helmet-async';
import React from 'react';

const SITE_NAME = 'CreaTech Squads';
const BASE_URL = 'https://createchsquads.com';
const DEFAULT_IMAGE = `${BASE_URL}/assets/Picsart.png`;
const COMPANY_EMAIL = 'Info@createchsquads.com';
const COMPANY_PHONE = '+919278139919';

const SEO = ({
    title,
    description = 'CreaTech Squads is a leading SEO, Digital Marketing & IT company in India offering website development, ERP, CRM and cyber security solutions to grow your business online.',
    keywords = 'SEO company in India, Digital Marketing company, Website Development, ERP Solutions, CRM Development, Cyber Security Services, IT Company India',
    canonical,
    ogType = 'website',
    ogImage = DEFAULT_IMAGE,
    author = 'CreaTech Squads',
    ogTitle = 'CreaTech Squads | SEO & Digital Growth Experts',
    ogDescription = 'Performance-driven SEO, digital marketing, web development and cyber security solutions across India.',
    publishedAt,
    modifiedAt,
    noindex = false,
    schemaData = null,
    breadcrumbs = [],
}) => {
    const defaultFullTitle = 'CreaTech Squads | SEO, Digital Marketing & IT Company in India';
    const fullTitle = (title && title.trim()) ? `${title} | ${SITE_NAME}` : defaultFullTitle;
    const finalOgTitle = title ? fullTitle : ogTitle;
    const finalOgDescription = title ? description : ogDescription;
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

    // Base Organization Schema
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": SITE_NAME,
        "url": BASE_URL,
        "logo": `${BASE_URL}/assets/logo1.png`,
        "sameAs": [
            "https://facebook.com/createchsquads",
            "https://www.youtube.com/@CreaTechSquads",
            "https://www.instagram.com/createchsquads",
            "https://www.linkedin.com/company/createchsquads"
        ],
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": COMPANY_PHONE,
            "contactType": "customer service",
            "areaServed": "IN",
            "availableLanguage": "en"
        }
    };

    // Website Schema with SearchAction
    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": SITE_NAME,
        "url": BASE_URL,
        "potentialAction": {
            "@type": "SearchAction",
            "target": `${BASE_URL}/blogs?q={search_term_string}`,
            "query-input": "required name=search_term_string"
        }
    };

    // BreadcrumbList Schema
    const breadcrumbSchema = breadcrumbs.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.name,
            "item": crumb.item ? `${BASE_URL}${crumb.item}` : BASE_URL
        }))
    } : null;

    return (
        <Helmet>
            {/* Primary */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content={author} />
            <link rel="canonical" href={canonicalUrl} />
            <meta name="robots" content={noindex ? "noindex,nofollow" : "index, follow"} />

            {/* Open Graph */}
            <meta property="og:title" content={finalOgTitle} />
            <meta property="og:description" content={finalOgDescription} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:type" content={ogType} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:site_name" content={SITE_NAME} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />

            {/* GEO / E-E-A-T / Local SEO */}
            <meta name="geo.region" content="IN-UP" />
            <meta name="geo.placename" content="Kanpur" />
            <meta name="geo.position" content="26.3986;80.3298" />
            <meta name="ICBM" content="26.3986, 80.3298" />
            
            {author && <meta name="article:author" content={author} />}
            {publishedAt && <meta property="article:published_time" content={publishedAt} />}
            {modifiedAt && <meta property="article:modified_time" content={modifiedAt} />}

            {/* Structured Data (JSON-LD) */}
            <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(websiteSchema)}
            </script>
            {breadcrumbSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            )}
            {schemaData && (
                <script type="application/ld+json">
                    {JSON.stringify(schemaData)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
